import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Download, Upload, AlertTriangle, CheckCircle, Loader2, Database, Trash2, FileJson } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DataManagement() {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState("merge");
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [collectionToClear, setCollectionToClear] = useState("");
  const [clearing, setClearing] = useState(false);

  // Only admin can access this page
  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t("dataManagement.noPermission")}</AlertDescription>
        </Alert>
      </div>
    );
  }

  async function handleExport() {
    setExporting(true);
    try {
      var response = await fetch(API_URL + "/api/admin/export", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {}
      });
      
      if (!response.ok) {
        throw new Error("Export failed");
      }
      
      var data = await response.json();
      
      // Create and download the file
      var blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = "realestate_export_" + new Date().toISOString().split("T")[0] + ".json";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t("dataManagement.exportSuccess"));
    } catch (err) {
      console.error(err);
      toast.error(t("dataManagement.exportError"));
    } finally {
      setExporting(false);
    }
  }

  function handleFileSelect(e) {
    var file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportResults(null);
    }
  }

  async function handleImport() {
    if (!importFile) {
      toast.error(t("dataManagement.selectFile"));
      return;
    }
    
    setImporting(true);
    setImportResults(null);
    
    try {
      var fileContent = await importFile.text();
      var jsonData = JSON.parse(fileContent);
      
      // Extract data from export format
      var importPayload = {
        data: jsonData.data || jsonData,
        mode: importMode
      };
      
      var response = await fetch(API_URL + "/api/admin/import", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: "Bearer " + token } : {})
        },
        body: JSON.stringify(importPayload)
      });
      
      if (!response.ok) {
        throw new Error("Import failed");
      }
      
      var result = await response.json();
      setImportResults(result.results);
      toast.success(t("dataManagement.importSuccess"));
      setShowImportDialog(false);
    } catch (err) {
      console.error(err);
      toast.error(t("dataManagement.importError") + ": " + err.message);
    } finally {
      setImporting(false);
    }
  }

  async function handleClearCollection() {
    if (!collectionToClear) return;
    
    setClearing(true);
    try {
      var response = await fetch(API_URL + "/api/admin/clear-collection/" + collectionToClear, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {}
      });
      
      if (!response.ok) {
        var error = await response.json();
        throw new Error(error.detail || "Clear failed");
      }
      
      var result = await response.json();
      toast.success(result.message);
      setShowClearDialog(false);
      setCollectionToClear("");
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    } finally {
      setClearing(false);
    }
  }

  var collections = [
    { value: "properties", label: t("nav.properties") },
    { value: "units", label: t("nav.units") },
    { value: "assets", label: t("nav.assets") },
    { value: "documents", label: t("nav.documents") },
    { value: "contracts", label: t("nav.contracts") },
    { value: "services", label: t("nav.services") },
    { value: "rentals", label: t("nav.rentals") },
    { value: "keys", label: t("nav.keys") },
    { value: "reminders", label: t("nav.reminders") },
    { value: "suppliers", label: t("nav.suppliers") }
  ];

  return (
    <div className="space-y-6" data-testid="data-management-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("dataManagement.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("dataManagement.subtitle")}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Card */}
        <Card data-testid="export-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-500" />
              {t("dataManagement.exportData")}
            </CardTitle>
            <CardDescription>{t("dataManagement.exportDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Database className="h-4 w-4" />
              <AlertDescription>{t("dataManagement.exportIncludes")}</AlertDescription>
            </Alert>
            <Button 
              onClick={handleExport} 
              disabled={exporting}
              className="w-full"
              data-testid="export-btn"
            >
              {exporting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("dataManagement.exporting")}</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />{t("dataManagement.downloadExport")}</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Import Card */}
        <Card data-testid="import-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-500" />
              {t("dataManagement.importData")}
            </CardTitle>
            <CardDescription>{t("dataManagement.importDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{t("dataManagement.importWarning")}</AlertDescription>
            </Alert>
            <Button 
              onClick={function() { setShowImportDialog(true); }}
              variant="outline"
              className="w-full"
              data-testid="import-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t("dataManagement.startImport")}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Import Results */}
      {importResults && (
        <Card data-testid="import-results">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              {t("dataManagement.importResults")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.keys(importResults.imported).length > 0 && (
                <div>
                  <h4 className="font-medium text-green-600 mb-2">{t("dataManagement.imported")}</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(importResults.imported).map(function([coll, count]) {
                      return (
                        <Badge key={coll} variant="outline" className="bg-green-50 text-green-700">
                          {coll}: {count}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
              {Object.keys(importResults.skipped).length > 0 && (
                <div>
                  <h4 className="font-medium text-yellow-600 mb-2">{t("dataManagement.skipped")}</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(importResults.skipped).map(function([coll, reason]) {
                      return (
                        <Badge key={coll} variant="outline" className="bg-yellow-50 text-yellow-700">
                          {coll}: {reason}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
              {Object.keys(importResults.errors).length > 0 && (
                <div>
                  <h4 className="font-medium text-red-600 mb-2">{t("dataManagement.errors")}</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(importResults.errors).map(function([coll, error]) {
                      return (
                        <Badge key={coll} variant="outline" className="bg-red-50 text-red-700">
                          {coll}: {error}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clear Collection Card */}
      <Card className="border-destructive/50" data-testid="clear-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" />
            {t("dataManagement.clearCollection")}
          </CardTitle>
          <CardDescription>{t("dataManagement.clearDescription")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t("dataManagement.clearWarning")}</AlertDescription>
          </Alert>
          <div className="flex gap-4">
            <Select value={collectionToClear} onValueChange={setCollectionToClear}>
              <SelectTrigger className="flex-1" data-testid="clear-collection-select">
                <SelectValue placeholder={t("dataManagement.selectCollection")} />
              </SelectTrigger>
              <SelectContent>
                {collections.map(function(coll) {
                  return (
                    <SelectItem key={coll.value} value={coll.value}>{coll.label}</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button 
              variant="destructive"
              onClick={function() { if (collectionToClear) setShowClearDialog(true); }}
              disabled={!collectionToClear}
              data-testid="clear-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t("dataManagement.clear")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>{t("dataManagement.importData")}</DialogTitle>
            <DialogDescription>{t("dataManagement.importDialogDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("dataManagement.selectJsonFile")}</Label>
              <Input 
                type="file" 
                accept=".json"
                onChange={handleFileSelect}
                data-testid="import-file-input"
              />
              {importFile && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <FileJson className="w-4 h-4" />
                  {importFile.name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t("dataManagement.importMode")}</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="import-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">{t("dataManagement.modeMerge")}</SelectItem>
                  <SelectItem value="replace">{t("dataManagement.modeReplace")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {importMode === "merge" ? t("dataManagement.modeMergeDesc") : t("dataManagement.modeReplaceDesc")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowImportDialog(false); }}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleImport} disabled={!importFile || importing} data-testid="confirm-import-btn">
              {importing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("dataManagement.importing")}</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />{t("dataManagement.import")}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent data-testid="clear-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">{t("dataManagement.confirmClear")}</DialogTitle>
            <DialogDescription>
              {t("dataManagement.confirmClearDesc", { collection: collectionToClear })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowClearDialog(false); }}>
              {t("common.cancel")}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearCollection}
              disabled={clearing}
              data-testid="confirm-clear-btn"
            >
              {clearing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("dataManagement.clearing")}</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" />{t("dataManagement.clearConfirm")}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
